import { Flex, Heading, Input, NativeSelectField, NativeSelectRoot, Skeleton, Spacer } from "@chakra-ui/react"
import {
  addDays,
  convertYYYYMMDDToDate,
  dateDiffInDays,
  dateOverride,
  endOfDay,
  formatYYYYMMDD,
  IANATimeZone,
  nowDateTime,
} from "@hydrologiq/common/helpers/date"
import { ReactNode, useEffect, useState } from "react"
import { Granularity } from "@hydrologiq/common/types/reporting/Granularity"
import { DateTime, zone } from "timezonecomplete"
import { Field } from "../chakra/Field"
import { toKey } from "../test/toKey"

/**
 * Re-anchor a calendar date (parsed as a zoneless/UTC `DateTime` by
 * `convertYYYYMMDDToDate`) to the *user's* timezone, at the start of that day.
 *
 * The date picker yields a `YYYY-MM-DD` string that `convertYYYYMMDDToDate`
 * turns into UTC midnight. Sending that straight to the backend (which buckets
 * in the user's IANA zone) put the first bucket at 01:00+01:00 instead of
 * 00:00+01:00. Reinterpreting the same Y/M/D as local wall-clock midnight makes
 * the download/preview start at the user's local midnight.
 */
function toLocalStartOfDay(date: DateTime): DateTime {
  return dateOverride(date, date.year(), date.month(), date.day(), 0, 0, 0, 0, zone(IANATimeZone()))
}

export interface ConfigureRangeProps {
  min: DateTime
  max: DateTime
  end?: DateTime
  granularityOptions?: Granularity[]
  granularityDefault?: Granularity
  callback?: (granularity: Granularity, start: DateTime, end: DateTime) => void
}

const getRealEndDate = (endDate: DateTime) => {
  const dateNow = nowDateTime()
  return dateNow.unixUtcMillis() > endDate.unixUtcMillis() ? endDate : dateNow
}

export function ConfigureRangeSkeleton({
  frequency,
  start,
  end,
}: {
  frequency?: ReactNode
  start?: ReactNode
  end?: ReactNode
}) {
  return (
    <>
      <Heading as="h2" size={"md"} mt={1} mb={1}>
        Select data frequency and time period
      </Heading>
      <Flex direction={{ base: "column", lg: "row" }}>
        <Field label="Frequency">{frequency ? frequency : <Skeleton height="40px" width="100%" />}</Field>
        <Spacer m={{ base: 1, lg: 5 }} />
        <Field label="Start" height="100%">
          {start ? start : <Skeleton height="40px" width="100%" />}
        </Field>
        <Spacer m={{ base: 1, lg: 5 }} />
        <Field label="End" height="100%">
          {end ? end : <Skeleton height="40px" width="100%" />}
        </Field>
      </Flex>
    </>
  )
}

function ConfigureRange({
  min: defaultStart,
  max: defaultEnd,
  end: defaultEndOverride,
  callback,
  granularityDefault = Granularity.Weekly,
  granularityOptions = [
    Granularity.Monthly,
    Granularity.Weekly,
    Granularity.Daily,
    Granularity.Hourly,
    Granularity.FiveMinute,
  ],

}: ConfigureRangeProps) {
  const [startFormatted, setStartFormatted] = useState<string>(formatYYYYMMDD(defaultStart))
  const [fromEndFormatted, _] = useState<string>(formatYYYYMMDD(getRealEndDate(defaultEnd)))
  const [endFormatted, setEndFormatted] = useState<string>(formatYYYYMMDD(getRealEndDate(defaultEndOverride ?? defaultEnd)))
  const [start, setStart] = useState<string>(startFormatted)
  const [end, setEnd] = useState<string>(endFormatted)
  const [granularity, setGranularity] = useState<string>(granularityDefault)

  const defaultMaxRange = 365 - 1
  const fiveMinuteMaxRange = 21 - 1

  useEffect(() => {
    if (granularity && endFormatted) updateFormattedEnd(endFormatted)
  }, [granularity])

  useEffect(() => {
    if (start && endFormatted) updateFormattedEnd(endFormatted)
    if (start && startFormatted) setStartFormatted(start)
  }, [start])

  useEffect(() => {
    const restrictedEnd = restrictedEndDate(end)
    if (callback && start && end && granularity) {
      const startDate = convertYYYYMMDDToDate(start)
      let endDate = convertYYYYMMDDToDate(restrictedEnd)
      if (startDate && endDate) {
        // Anchor the selected calendar days to the user's timezone, so the
        // window runs local-midnight → local-end-of-day (not UTC midnight,
        // which the backend would bucket at 01:00+01:00). See toLocalStartOfDay.
        const localStart = toLocalStartOfDay(startDate)
        const localEnd = endOfDay(toLocalStartOfDay(endDate))
        callback(granularity as Granularity, localStart, localEnd)
      }
    }
    setEnd(restrictedEnd)
  }, [start, end, granularity])

  const updateFormattedEnd = (date: string) => {
    let startDate = convertYYYYMMDDToDate(start)
    let formattedEnd = date
    if (granularity && startDate) {
      let newDate: DateTime | undefined = undefined
      switch (granularity) {
        case Granularity.Hourly:
        case Granularity.FiveMinute:
          newDate = addDays(startDate, fiveMinuteMaxRange)
          break
        default:
          newDate = addDays(startDate, defaultMaxRange)
          break
      }
      if (newDate) {
        if (newDate.greaterThan(defaultEnd)) newDate = defaultEnd
        formattedEnd = formatYYYYMMDD(newDate)
      }
      if (formattedEnd !== date) {
        setEndFormatted(formattedEnd)
      }
    }
  }

  const restrictedEndDate = (date: string) => {
    const startDate = convertYYYYMMDDToDate(start)
    const endDate = convertYYYYMMDDToDate(date)

    if (startDate && endDate) {
      const startDiff = dateDiffInDays(startDate, endDate)
      const endDiff = dateDiffInDays(endDate, startDate)
      let newDate: DateTime | undefined = undefined
      switch (granularity) {
        case Granularity.Hourly:
        case Granularity.FiveMinute:
          if (startDiff >= fiveMinuteMaxRange || endDiff >= fiveMinuteMaxRange)
            newDate = addDays(startDate, fiveMinuteMaxRange)
          break
        default:
          if (startDiff >= defaultMaxRange || endDiff >= defaultMaxRange) newDate = addDays(startDate, defaultMaxRange)
          break
      }
      if (newDate) {
        if (newDate.greaterThan(defaultEnd)) newDate = defaultEnd
        return formatYYYYMMDD(newDate)
      }
    }
    return date
  }

  const updateGranularity = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault()
    if (granularity && e.target.value !== granularity) setEndFormatted(formatYYYYMMDD(getRealEndDate(defaultEnd)))
    setGranularity(e.target.value)
  }

  const updateStart = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    let date = e.target.value
    const convertedDate = convertYYYYMMDDToDate(date)
    const endDate = convertYYYYMMDDToDate(endFormatted)
    let newEnd: DateTime | undefined = undefined
    if (convertedDate && endDate && convertedDate < defaultEnd) {
      const dateDiff = dateDiffInDays(convertedDate, endDate)
      if (dateDiff != 0) {
        switch (granularity) {
          case Granularity.Hourly:
          case Granularity.FiveMinute:
            newEnd =
              dateDiff < 0 || dateDiff > fiveMinuteMaxRange
                ? getRealEndDate(addDays(convertedDate, fiveMinuteMaxRange))
                : undefined
            break
          default:
            newEnd =
              dateDiff < 0 || dateDiff > defaultMaxRange
                ? getRealEndDate(addDays(convertedDate, defaultMaxRange))
                : undefined
            break
        }
      }
    }
    date = date > end && newEnd !== undefined && date > formatYYYYMMDD(newEnd) ? start : date
    if (newEnd) {
      if (newEnd.greaterThan(defaultEnd)) newEnd = defaultEnd
      setEnd(formatYYYYMMDD(newEnd))
    }
    date = date < formatYYYYMMDD(defaultStart) || date > formatYYYYMMDD(defaultEnd) ? start : date
    setStart(date)
  }

  const updateEnd = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    const date = e.target.value

    let newEndDate = date < start ? end : date
    setEnd(restrictedEndDate(newEndDate))
  }

  return (
    <ConfigureRangeSkeleton
      frequency={
        <NativeSelectRoot>
          <NativeSelectField onChange={updateGranularity} value={granularity}>
            {granularityOptions &&
              granularityOptions.map((key) => {
                return (
                  <option value={key.toString()} key={toKey(key)}>
                    {key}
                  </option>
                )
              })}
          </NativeSelectField>
        </NativeSelectRoot>
      }
      start={
        <Input
          onChange={updateStart}
          type="date"
          min={formatYYYYMMDD(defaultStart)}
          max={fromEndFormatted}
          value={start}
        />
      }
      end={<Input onChange={updateEnd} type="date" min={startFormatted} max={endFormatted} value={end} />}
    />
  )
}

export default ConfigureRange
